import type { PageNumber } from "@pdfking/renderer-api"
import { NullQ } from "@pdfking/structs"
import { RecordCreate, RecordMap, SortBy } from "@pdfking/tfunc"

import type { Numbering, SheetInfo } from "#internal"
import type { DestinationID, ImposeResult, PageInfo, Sheet } from "#public"

/** What the numbered sheets add up to: every printed page, the pages each
 * destination prints, and each sheet's destinations — the slots it prints for
 * each — with their division. */
export const ComputeImpositionResult = (
  sheet_infos: SheetInfo[],
  numbering: Numbering
): Pick<ImposeResult, "pages" | "destinations"> & {
  sheet_destinations: Sheet["destinations"][]
  sheet_divisions: Sheet["divisions"][]
} => {
  const destination_pages = new Map<DestinationID, Set<PageNumber>>()

  // Every destination on a sheet prints all of its pages.
  const sheet_pages = sheet_infos.map(sheet => {
    const printed = sheet.slots.flatMap(slot =>
      slot.pages.flatMap(path => {
        const page_number = numbering.page_number_of(path)
        return NullQ(page_number) ? [] : [page_number]
      })
    )
    for (const d of sheet.destinations) {
      const id = d.id as DestinationID
      const pages = destination_pages.get(id) ?? new Set()
      for (const page of printed) pages.add(page)
      destination_pages.set(id, pages)
    }
    return printed
  })

  const destinations = RecordCreate(
    SortBy([...destination_pages.keys()]).map(id => [id, SortBy([...destination_pages.get(id)!])])
  )
  // A slot is a page's place in its destination's list, known only once
  // every sheet is counted.
  const slot_of = RecordMap(
    (pages: PageNumber[]) => new Map(pages.map((page, i) => [page, (i + 1) as PageNumber])),
    destinations
  )

  return {
    pages: RecordCreate(
      numbering.printed.map(
        ({ page_number, size, is_front, meta }) =>
          [
            page_number,
            {
              width: size.width,
              height: size.height,
              is_front,
              print_front: meta.print_front,
              print_rear: meta.print_rear,
            } satisfies PageInfo,
          ] as const
      )
    ),
    // Keyed in id order: a destination's print layer order is its place here.
    destinations,
    sheet_destinations: sheet_infos.map((sheet, i) =>
      RecordCreate(
        sheet.destinations.map(d => {
          const id = d.id as DestinationID
          return [id, sheet_pages[i]!.map(page => slot_of[id]!.get(page)!)] as const
        })
      )
    ),
    sheet_divisions: sheet_infos.map(sheet =>
      RecordCreate(
        sheet.destinations.map(
          (d, i) => [d.id as DestinationID, sheet.destination_divisions[i]!] as const
        )
      )
    ),
  }
}
